import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import {
	checkboxChoiceGroup,
	checkboxOptionLabel,
} from "../../utils/checkboxChoiceGroup";

const useStyles = makeStyles(() => ({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: 16,
	},
	statusRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		color: "#009A17",
		fontSize: 14,
		fontWeight: 500,
	},
	answerBlock: {
		paddingBottom: 12,
		borderBottom: "1px solid #e5e7eb",
		"&:last-child": { borderBottom: "none", paddingBottom: 0 },
	},
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: "#6b7280",
		textTransform: "uppercase",
		letterSpacing: "0.04em",
		marginBottom: 4,
	},
	value: {
		fontSize: 15,
		color: "#111827",
		wordBreak: "break-word",
	},
	emptyHint: {
		fontSize: 14,
		color: "#6b7280",
		lineHeight: 1.5,
	},
}));

const formatDisplayValue = (fieldType, value) => {
	if (!value) return "";
	if (fieldType === "checkbox") return value === "true" ? "Yes" : "No";
	if (fieldType === "signature" && value.startsWith("data:")) return value;
	return value;
};

export default function SubmittedAnswersReview({ fields = [], values = [] }) {
	const classes = useStyles();

	const answers = useMemo(() => {
		const byId = new Map((values || []).map((v) => [String(v.id), v]));
		return (fields || [])
			.map((f) => {
				const id = String(f.id || f.fieldId);
				const fromValues = byId.get(id);
				const raw =
					fromValues?.value ||
					f.fieldValue ||
					f.value ||
					"";
				const fieldType =
					fromValues?.fieldType || f.fieldType || f.type || "shortText";
				const option =
					fromValues?.label ||
					f.title ||
					f.fieldTitle ||
					f.fieldDescription ||
					"Field";
				const question = checkboxChoiceGroup(fromValues || f);
				const label = question ? `${question}: ${checkboxOptionLabel(fromValues || f)}` : option;
				const trimmed = String(raw).trim();
				const display = formatDisplayValue(fieldType, trimmed);
				return {
					id,
					label,
					fieldType,
					display,
					isSignatureImage:
						fieldType === "signature" && display.startsWith("data:"),
				};
			})
			.filter((a) => a.display);
	}, [fields, values]);

	return (
		<div className={classes.root}>
			<div className={classes.statusRow}>
				<CheckCircleOutlineIcon fontSize="small" />
				<span>Submitted — all required fields were saved</span>
			</div>
			{answers.length > 0 ? (
				answers.map((a) => (
					<div key={a.id} className={classes.answerBlock}>
						<Typography className={classes.label}>{a.label}</Typography>
						{a.isSignatureImage ? (
							<img src={a.display} alt="Signature" style={{ maxHeight: 64, maxWidth: "100%" }} />
						) : (
							<Typography className={classes.value}>{a.display}</Typography>
						)}
					</div>
				))
			) : (
				<Typography className={classes.emptyHint}>
					Your answers are embedded in the PDF on the left. Scroll inside the document
					viewer (mouse wheel or trackpad), or use the toolbar page arrows (e.g. 1 / 2).
				</Typography>
			)}
		</div>
	);
}
